import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import Cookies from "js-cookie";

import { SuccessMessage, ErrorMessage } from "./AllConfirmationMessages";
import { useCustomDispatch, useCustomLocation } from "../../hooks";
import { updatePatientProfile } from "../../services/apis/setting/updatePatientProfile";
import { useAuthSelector } from "../../services/selectors/authSelector";
import {
  initiateVideoCallToPharmacist,
  clearVideoCallInitiate,
  selectInitiateVideoCallToPharmacistData,
} from "../../services/slices/supervisor/videoCallInitiate";
import { useSelector } from "react-redux";

import avatar from "../../assets/images/userimg.png";
import * as Icons from "../../assets/svg/icons";
import * as EndPoints from "../../networking/urlEndPoints";
import * as routeName from "../../routes/routeName";

import VideoCall from "../video-calling/VideoCall";
import VideoCallErrorBoundary from "../video-calling/VideoCallErrorBoundary";
import {
  publishVideoCallRequest,
  publishVideoCallCancelled,
  publishVideoCallEnded,
  subscribeToVideoCallResponse,
  subscribeToVideoCallSession,
} from "../video-calling/videoCallHelper";
import {
  cancelDriverDeliveryVideoCall,
  endDriverDeliveryVideoCall,
} from "../video-calling/deliveryVideoCallApi";
import { saveDeliveryCallImage } from "../../services/slices/supervisor/videoCallInitiate";
import {
  getSafeExternalUrl,
  getSafeTelHref,
} from "../../utils/helper/security";
import { normalizeBackendAssetUrl } from "../../utils/helper/url";
import {
  maskPatientAddress,
  maskPatientNamePart,
  maskPatientPhoneLike,
  shouldMaskPatientDetails,
} from "../../utils/helper/patientMasking";

/** Get patient initials: last name + first name (e.g. "GS", "H") */
const getPatientInitials = (lastName, firstName) => {
  const last = (lastName || "").trim().charAt(0).toUpperCase();
  const first = (firstName || "").trim().charAt(0).toUpperCase();
  if (last && first) return `${last}${first}`;
  if (last) return last;
  if (first) return first;
  return "?";
};

/** True when image is empty or the default static user image from API/assets */
const isDefaultUserImage = (src) => {
  if (!src || typeof src !== "string") return true;
  return /user\.png|userimg/i.test(src) || src.includes("userimg");
};

/** Badge color palette (bg, text) – same patient gets same color */
const INITIALS_BADGE_COLORS = [
  { bg: "#E0F2F7", text: "#1A237E" },
  { bg: "#FCE4EC", text: "#880E4F" },
  { bg: "#E8F5E9", text: "#1B5E20" },
  { bg: "#FFF8E1", text: "#E65100" },
  { bg: "#E3F2FD", text: "#0D47A1" },
];

const getInitialsBadgeStyle = (lastName, firstName) => {
  const key = `${lastName || ""}-${firstName || ""}`;
  let index = 0;
  for (let i = 0; i < key.length; i++) index += key.charCodeAt(i);
  const { bg, text } =
    INITIALS_BADGE_COLORS[Math.abs(index) % INITIALS_BADGE_COLORS.length];
  return { backgroundColor: bg, color: text };
};

const getPatientPreferredValue = (data) => {
  const preferredKeys = [
    "preferred_name",
    "prefered_name",
    "preffered_name",
    "patient_preferred_name",
    "preferred",
    "prefered",
    "preffered",
    "perferred",
  ];

  for (const key of preferredKeys) {
    const value = data?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }

  return "";
};

const PatientDetailsContext = createContext({ patientData: null });

export const usePatientDetailsContext = () => useContext(PatientDetailsContext);

export const displaySelectedImage = (
  event,
  setSelectedImage,
  index,
  setShowToast,
  data,
  dispatch,
) => {
  if (event.target.files.length > 0) {
    const file = event.target.files[0];

    const requestedData = {
      pharmacy_id: data.pharmacy_id,
      patient_id: data.id,
      profile_image: file,
    };
    dispatch(updatePatientProfile(requestedData));

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();

      reader.onload = function (e) {
        setSelectedImage(e.target.result, index);
        setShowToast((prevState) => ({ ...prevState, successToast: true }));

        setTimeout(() => {
          setShowToast((prevState) => ({ ...prevState, successToast: false }));
        }, 2000);
      };

      reader.readAsDataURL(file);
    }
  }
};

export const PatientDetails = ({
  data,
  totalRxData,
  index,
  patientImg,
  openIndex,
  onDropdownToggle,
  children,
  headerRight,
	  headerAction,
  profileImageInputId,
  alwaysShowChildren,
  hideVideoCallButton = false,
  hidePreferredNameAction = false,
}) => {
  const profileInputId = profileImageInputId ?? `selectProfileImage-${index}`;
  const dispatch = useCustomDispatch();
  const location = useCustomLocation();
  const {
    driverID,
    authLoginResponse,
    pharmacyId,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
  } = useAuthSelector();

  const normalizedProfileImage = normalizeBackendAssetUrl(
    data?.profile_image || patientImg,
  );
  const [selectedImage, setSelectedImage] = useState(
    normalizedProfileImage || avatar,
  );
  const [showToast, setShowToast] = useState({ successToast: "" });
  const safeMapUrl = getSafeExternalUrl(data?.map_url);
  const safePhoneHref = getSafeTelHref(data?.phone);
  const preferredNameInputRef = useRef(null);
  const nativePrewarmRef = useRef({ key: null, postedAt: 0 });
  // Holds the payload needed to tear down the current prewarm (so the unmount
  // cleanup below can fire it even after props have changed). Set when a prewarm
  // is posted, cleared when torn down.
  const prewarmTeardownInfoRef = useRef(null);
  const preferredValue = getPatientPreferredValue(data);
  const driverName =
    authLoginResponse?.data?.data?.driver_profile?.name ||
    authLoginResponse?.data?.data?.email?.split("@")[0] ||
    "Driver";
  const postNativeVideoCallPrewarm = useCallback(() => {
    const inFlutterApp =
      typeof window !== "undefined" &&
      (window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        (window.location?.search || "").includes("flutter_app=1") ||
        sessionStorage?.getItem("flutter_app") === "1");
    if (
      !inFlutterApp ||
      isPharmacistForSelectedPharmacy ||
      !window.DriverApp ||
      typeof window.DriverApp.postMessage !== "function" ||
      !pharmacyId ||
      !driverID ||
      data?.id == null ||
      !pharmacistIdForSelectedPharmacy
    ) {
      return false;
    }

    const prewarmKey = [
      data.id,
      pharmacyId,
      pharmacistIdForSelectedPharmacy,
    ]
      .map((value) => String(value || "").trim())
      .join("|");
    const now = Date.now();
    if (
      nativePrewarmRef.current.key === prewarmKey &&
      now - nativePrewarmRef.current.postedAt < 1500
    ) {
      return true;
    }

    try {
      window.DriverApp.postMessage(
        JSON.stringify({
          type: "videoCallPrewarmPatient",
          patientId: String(data.id),
          patientName: `${data?.last_name || ""} ${data?.first_name || ""}`.trim(),
          patientPreferredName: data?.preferred_name || preferredValue || "",
          pharmacyId: String(pharmacyId),
          driverID: String(driverID),
          driverName,
          pharmacistId: String(pharmacistIdForSelectedPharmacy),
          pharmacistName: pharmacistNameForSelectedPharmacy || "Pharmacist",
          authToken: Cookies.get("authToken") ?? null,
          ablyApiKey: EndPoints.apiKey || null,
        }),
      );
      nativePrewarmRef.current = { key: prewarmKey, postedAt: now };
      prewarmTeardownInfoRef.current = {
        patientId: String(data.id),
        pharmacyId: String(pharmacyId),
        driverID: String(driverID),
        pharmacistId: String(pharmacistIdForSelectedPharmacy),
      };
      return true;
    } catch (error) {
      console.error("Video Call: Failed to post native prewarm payload", error);
      return false;
    }
  }, [
    data?.id,
    data?.first_name,
    data?.last_name,
    data?.preferred_name,
    driverID,
    driverName,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
    pharmacyId,
    preferredValue,
  ]);

  // Tear down a prewarmed call when the driver moves on. Tells native to drop
  // the pre-joined session (and the pharmacist's silent pre-join) so nothing is
  // left connected/muted-in-session after a collapse, patient switch, or the
  // card being removed (Deliver/Missed reloads the list). No-op unless a prewarm
  // is currently active for this card. Stable identity (reads from the ref).
  const postNativeVideoCallPrewarmTeardown = useCallback((reason) => {
    const info = prewarmTeardownInfoRef.current;
    prewarmTeardownInfoRef.current = null;
    const inFlutterApp =
      typeof window !== "undefined" &&
      (window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        (window.location?.search || "").includes("flutter_app=1") ||
        sessionStorage?.getItem("flutter_app") === "1");
    if (
      !info ||
      !inFlutterApp ||
      !window.DriverApp ||
      typeof window.DriverApp.postMessage !== "function"
    ) {
      return false;
    }
    try {
      window.DriverApp.postMessage(
        JSON.stringify({
          type: "videoCallPrewarmTeardown",
          patientId: info.patientId,
          pharmacyId: info.pharmacyId,
          driverID: info.driverID,
          pharmacistId: info.pharmacistId,
          reason: reason || "collapsed",
        }),
      );
      return true;
    } catch (error) {
      console.error("Video Call: Failed to post native prewarm teardown", error);
      return false;
    }
  }, []);

  useEffect(() => {
    const isOpen = alwaysShowChildren || openIndex === index;
    if (!isOpen) {
      // Collapsed or switched to another patient: drop the pre-join.
      if (nativePrewarmRef.current.key) {
        postNativeVideoCallPrewarmTeardown("collapsed");
      }
      nativePrewarmRef.current = { key: null, postedAt: 0 };
      return;
    }
    const timer = window.setTimeout(() => {
      postNativeVideoCallPrewarm();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [
    alwaysShowChildren,
    data?.id,
    index,
    openIndex,
    pharmacistIdForSelectedPharmacy,
    pharmacyId,
    postNativeVideoCallPrewarm,
    postNativeVideoCallPrewarmTeardown,
  ]);

  // Deliver/Missed reloads the list, which unmounts this card without a collapse
  // transition — tear down any still-active prewarm on unmount too.
  useEffect(() => {
    return () => {
      if (prewarmTeardownInfoRef.current) {
        postNativeVideoCallPrewarmTeardown("unmounted");
      }
    };
  }, [postNativeVideoCallPrewarmTeardown]);

  // const deliveredDispenseRoute = location.pathname === routeName.DRIVER_ROUTE_DELIVERED;
  // const pendingPharmacistSignRoute = location.pathname === routeName.pending_pharmacist_signatures;
  // const CounsellingRoute = location.pathname === routeName.Counselling;
  // const showContractButton =
  //     driverSelectedPharmacy?.pharmacies?.transfer_concent === 1 &&
  //     !deliveredDispenseRoute &&
  //     !pendingPharmacistSignRoute &&
  //     !CounsellingRoute &&
  //     data?.pharmacy_transfer_concents != null;

  useEffect(() => {
    const hasLocalPreview =
      typeof selectedImage === "string" &&
      /^(data:|blob:)/i.test(selectedImage);
    if (!hasLocalPreview) {
      setSelectedImage(normalizedProfileImage || avatar);
    }
  }, [normalizedProfileImage]);

  const showInitialsBadge = isDefaultUserImage(selectedImage);
  const initials = getPatientInitials(data?.last_name, data?.first_name);
  const initialsStyle = getInitialsBadgeStyle(
    data?.last_name,
    data?.first_name,
  );
  const shouldMaskDetails = shouldMaskPatientDetails(pharmacyId);
  const displayLastName = shouldMaskDetails
    ? maskPatientNamePart(data?.last_name, { wrap: true })
    : data?.last_name;
  const displayFirstName = shouldMaskDetails
    ? maskPatientNamePart(data?.first_name)
    : data?.first_name;
  const displayPhn = shouldMaskDetails
    ? maskPatientPhoneLike(data?.phn)
    : data?.phn;
  const displayPhone = shouldMaskDetails
    ? maskPatientPhoneLike(data?.phone)
    : data?.phone;
  const displayFullAddress = shouldMaskDetails
    ? maskPatientAddress(data?.full_address)
    : data?.full_address;
  const [preferredInputValue, setPreferredInputValue] = useState(
    preferredValue,
  );
  const [isPreferredNameSaved, setIsPreferredNameSaved] = useState(
    Boolean(preferredValue),
  );
  const [preferredNameSaving, setPreferredNameSaving] = useState(false);
  const [preferredNameError, setPreferredNameError] = useState("");
  const [isPreferredNameEditing, setIsPreferredNameEditing] = useState(false);
  const displayedPreferredName = isPreferredNameSaved
    ? preferredInputValue.trim() || preferredValue
    : preferredValue;

  useEffect(() => {
    setPreferredInputValue(preferredValue);
    setIsPreferredNameSaved(Boolean(preferredValue));
    if (preferredValue) {
      setIsPreferredNameEditing(false);
    }
  }, [preferredValue]);

  useEffect(() => {
    if (isPreferredNameEditing && preferredNameInputRef.current) {
      preferredNameInputRef.current.focus();
    }
  }, [isPreferredNameEditing]);

  const handlePreferredNameSave = async () => {
    const nextPreferredName = preferredInputValue.trim();
    if (!nextPreferredName || preferredNameSaving) return;

    setPreferredNameSaving(true);
    setPreferredNameError("");

    try {
      await dispatch(updatePatientProfile({
        pharmacy_id: data.pharmacy_id || pharmacyId,
        patient_id: data.id,
        preferred_name: nextPreferredName,
      })).unwrap();

      setPreferredInputValue(nextPreferredName);
      setIsPreferredNameSaved(true);
      setIsPreferredNameEditing(false);
    } catch (error) {
      setPreferredNameError("Unable to update.");
    } finally {
      setPreferredNameSaving(false);
    }
  };

  const preferredNameAction = !hidePreferredNameAction && !isPreferredNameSaved && !isPreferredNameEditing ? (
    <button
      type="button"
      onClick={() => setIsPreferredNameEditing(true)}
      className="shrink-0 inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
    >
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      Add preferred name
    </button>
  ) : null;
  const handoverActionAvailable = Boolean(
    headerAction &&
    data?.delivery_status !== "delivered" &&
    data?.route_access?.allowed !== false
  );
  const showPreferredNameInHeaderActions = Boolean(
    preferredNameAction && (headerRight != null || headerAction != null)
  );
  const openProfileImagePicker = () => {
    document.getElementById(profileInputId)?.click();
  };

  return (
    <React.Fragment>
      {/* Header: avatar, name, badge, delivery time / external link */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-1.5 min-w-0 flex-1">
          <button
            type="button"
            onClick={openProfileImagePicker}
            className="relative rounded-full w-9 h-9 flex-shrink-0 overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            aria-label={`Upload or replace profile image for ${data?.last_name || ""} ${data?.first_name || ""}`.trim()}
            title="Upload or replace profile image"
          >
            {showInitialsBadge ? (
              <span
                className="w-full h-full flex items-center justify-center text-xs font-semibold uppercase"
                style={initialsStyle}
              >
                {initials}
              </span>
            ) : (
              <img
                src={selectedImage}
                alt=""
                className="w-full h-full object-cover bg-gray-200"
              />
            )}
          </button>
          <input
            name="avatar"
            accept="image/jpeg, image/jpg, image/png,capture=camera"
            onChange={(event) =>
              displaySelectedImage(
                event,
                setSelectedImage,
                index,
                setShowToast,
                data,
                dispatch,
              )
            }
            type="file"
            id={profileInputId}
            className="hidden"
          />
          <div className="min-w-0 flex-1">
            <h2 className="flex min-w-0 items-center gap-1 text-base font-semibold uppercase tracking-tight">
              <span className="min-w-0 truncate whitespace-nowrap">
                {displayLastName} {displayFirstName}
              </span>
              {totalRxData && (
                <span className="flex items-center justify-center h-5 w-5 shrink-0 text-xs rounded-full text-green-700 bg-green-100">
                  {totalRxData}
                </span>
              )}
            </h2>

            <div className="flex items-center justify-between gap-2 -mt-1">
              <p className="min-w-0 truncate whitespace-nowrap text-xs uppercase text-gray-400">
                {data?.gender && <span>{data.gender}</span>}
                {data?.gender && data?.dob && " · "}
                {data?.dob && <span>{data.dob}</span>}
              </p>

              {!showPreferredNameInHeaderActions ? preferredNameAction : null}
            </div>
          </div>
        </div>
		{handoverActionAvailable ? (
		  <div className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
			  {headerRight}
			  {headerAction}
		  </div>
		) : (headerRight != null || headerAction != null) ? (
		  <div className="flex items-start gap-2">
			  <div className="flex flex-col items-end gap-1">
				  {headerRight}
				  {showPreferredNameInHeaderActions ? preferredNameAction : null}
			  </div>
		  </div>
        ) : location.pathname === routeName.DRIVER_ROUTE_DELIVERED ? (
          <div
            style={{ fontSize: "10px" }}
            className="text-gray-400 flex items-center"
          >
            {data?.delivery_human_time}
          </div>
        ) : null}
      </div>

	  {handoverActionAvailable && showPreferredNameInHeaderActions ? (
		<div className="ml-[42px] mt-1 flex justify-end">
			{preferredNameAction}
		</div>
	  ) : null}

      {isPreferredNameSaved && displayedPreferredName && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 mt-1.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center overflow-hidden text-green-600 [&_svg]:!h-full [&_svg]:!w-full">
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </span>
            <div className="leading-tight min-w-0">
              <p
                className="text-xs text-gray-400 uppercase tracking-wide"
                style={{ fontSize: "10px" }}
              >
                Preferred name
              </p>
              <p className="text-xs text-gray-500 break-words uppercase">
                {displayedPreferredName}
              </p>
            </div>
          </div>
        </div>
      )}

      <AnimatePresence initial={false}>
        {!isPreferredNameSaved && isPreferredNameEditing && (
          <motion.div
            key="preferred-name-editor"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <motion.div
              initial={{ y: -6 }}
              animate={{ y: 0 }}
              exit={{ y: -6 }}
              transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 mt-1.5 space-y-1"
            >
              <p
                className="text-xs text-gray-400 uppercase tracking-wide"
                style={{ fontSize: "10px" }}
              >
                Preferred name
              </p>
              <div className="flex items-center gap-1.5">
                <input
                  ref={preferredNameInputRef}
                  id={`preferred-name-${data?.id || index}`}
                  name={`preferred-name-${data?.id || index}`}
                  type="text"
                  value={preferredInputValue}
                  onChange={(event) =>
                    setPreferredInputValue(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handlePreferredNameSave();
                    }
                    if (event.key === "Escape") {
                      setIsPreferredNameEditing(false);
                      setPreferredInputValue(preferredValue);
                      setPreferredNameError("");
                    }
                  }}
                  placeholder="Enter preferred name"
                  disabled={preferredNameSaving}
                  className="flex-1 min-w-0 h-7 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700 placeholder:text-gray-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={handlePreferredNameSave}
                  disabled={!preferredInputValue.trim() || preferredNameSaving}
                  aria-label="Save preferred name"
                  className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md bg-emerald-600 text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {preferredNameSaving ? (
                    <span className="text-[10px] leading-none">...</span>
                  ) : (
                    <svg
                      className="w-3.5 h-3.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsPreferredNameEditing(false);
                    setPreferredInputValue(preferredValue);
                    setPreferredNameError("");
                  }}
                  disabled={preferredNameSaving}
                  aria-label="Cancel"
                  className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 disabled:opacity-50"
                >
                  <Icons.XIcon className="w-3.5 h-3.5" color="currentColor" />
                </button>
              </div>
              {preferredNameError && (
                <p className="text-[10px] text-red-500">{preferredNameError}</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact: PHN, Phone – only show card if value exists */}
      {(data?.phn || data?.phone) && (
        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
          {data?.phn && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2">
              <div className="flex items-center gap-2">
                {/* ICON */}
                <span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center overflow-hidden text-green-600 [&_svg]:!h-full [&_svg]:!w-full">
                  <svg
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2v-8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-3 3c0 1.23 1.24 2.37 2.83 2.83M15 11h3m-3 4h2"
                    />
                  </svg>
                </span>

                {/* TEXT STACK */}
                <div className="leading-tight">
                  <p
                    className="text-xs text-gray-400 uppercase tracking-wide"
                    style={{ fontSize: "10px" }}
                  >
                    PHN
                  </p>
                  <p className="text-xs text-gray-500 break-all">
                    {displayPhn}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            {data?.phone && (
              <a href={safePhoneHref || "#"} className="w-full">
                <div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 w-full">
                  <div className="flex items-center gap-2">
                    {/* ICON */}
                    <span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center overflow-hidden text-green-600 [&_svg]:!h-full [&_svg]:!w-full">
                      <Icons.PhoneIcon color="#16a34a" />
                    </span>

                    {/* TEXT STACK */}
                    <div className="leading-tight">
                      <p
                        className="text-xs text-gray-400 uppercase tracking-wide"
                        style={{ fontSize: "10px" }}
                      >
                        PHONE
                      </p>
                      <p className="text-xs text-gray-500 break-all">
                        {displayPhone}
                      </p>
                    </div>
                  </div>
                </div>
              </a>
            )}
            {/* <VideoCallButton data={data} hide={hideVideoCallButton} /> */}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mt-1">
        {data?.full_address && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 py-1 px-2 ">
            <a
              target="_blank"
              href={safeMapUrl || "#"}
              rel="noopener noreferrer"
              key="mapLink"
            >
              <div className="flex items-center gap-2">
                <span className="inline-flex w-4 h-4 flex-shrink-0 items-center justify-center overflow-hidden text-sky-500 [&_svg]:!h-full [&_svg]:!w-full">
                  <Icons.LocationIcon color="#059669" />
                </span>

                <div className="leading-tight">
                  {/* <p
                                className="text-xs text-gray-400 uppercase tracking-wide"
                                style={{ fontSize: "10px" }}
                            >
                                ADDRESS
                            </p> */}
                  <p className="text-[10px] text-gray-500 break-words">
                    {displayFullAddress}
                  </p>
                </div>
              </div>
            </a>
          </div>
        )}

        {onDropdownToggle != null && (
          <button
            className="p-2.5 rounded-md bg-gray-100"
            onClick={() => {
              if (openIndex !== index) {
                postNativeVideoCallPrewarm();
              }
              onDropdownToggle(index, data);
            }}
            type="button"
          >
            {openIndex === index ? (
              <Icons.DropdownBoxIcon2 />
            ) : (
              <Icons.DropdownBoxIcon1 />
            )}
          </button>
        )}
      </div>

      {data?.delivey_note && (
        <p
          className="mt-1.5 bg-fuchsia-200 p-1 pl-1.5 rounded-md"
          style={{
            color: "black",
            fontSize: "13px",
            fontWeight: "bold",
            backgroundColor: "rgb(132 204 22)",
          }}
        >
          <span className="text-red-700">Note: </span>
          {data?.delivey_note}
        </p>
      )}

      {(alwaysShowChildren || openIndex === index) && (
        <PatientDetailsContext.Provider value={{ patientData: data }}>
          {children}
        </PatientDetailsContext.Provider>
      )}

      {showToast.successToast && (
        <label className="fixed -left-3 -right-0 bottom-0 z-50">
          <SuccessMessage
            message={"Profile Updated Successfully."}
            setShowToast={() =>
              setShowToast((prevState) => ({
                ...prevState,
                successToast: false,
              }))
            }
          />
        </label>
      )}
    </React.Fragment>
  );
};

/** @deprecated Use PatientDetails – action row (call, map, contract, dropdown) is now inside PatientDetails. Kept for searchPatientList which uses a custom card layout. */
export const VideoCallButton = ({
  data,
  hide = false,
  variant = "icon", // "icon" | "cta"
  disabled = false,
  className = "",
  label = "Video call",
}) => {
  const {
    driverID,
    authLoginResponse,
    pharmacyId,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
  } = useAuthSelector();

  const dispatch = useCustomDispatch();
  const videoCallInitiateData = useSelector(
    selectInitiateVideoCallToPharmacistData,
  );

  const [isVideoCallOpen, setIsVideoCallOpen] = useState(false);
  const [videoCallLoadingPatientId, setVideoCallLoadingPatientId] =
    useState(null);
  const [callState, setCallState] = useState("idle"); // 'idle', 'requesting', 'accepted', 'declined', 'ended'
  const [callStatusReason, setCallStatusReason] = useState(null); // e.g. 'busy' | 'timeout' | null
  const [hasCamera, setHasCamera] = useState(false);
  const [videoCallError, setVideoCallError] = useState(null); // Error message when video call fails

  const callStateRef = useRef("idle");

  const ablyRef = useRef(null);
  const responseAblyRef = useRef(null);
  const sessionAblyRef = useRef(null);

  const callInfoRef = useRef(null);
  const activeCallIdRef = useRef(null);
  const timeoutRef = useRef(null);
  const graceTimeoutRef = useRef(null);

  // Get driver name from auth
  const driverName =
    authLoginResponse?.data?.data?.driver_profile?.name ||
    authLoginResponse?.data?.data?.email?.split("@")[0] ||
    "Driver";
  const inFlutterApp =
    typeof window !== "undefined" &&
    (window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
      (window.location?.search || "").includes("flutter_app=1") ||
      sessionStorage?.getItem("flutter_app") === "1");
  const selectedPharmacistName =
    pharmacistNameForSelectedPharmacy ||
    authLoginResponse?.data?.data?.driver_pharmacist_user?.name ||
    null;
  const preferredValue = getPatientPreferredValue(data);
  const postNativeVideoCallPrewarm = (resolvedPharmacistId) => {
    if (
      !inFlutterApp ||
      typeof window === "undefined" ||
      !window.DriverApp ||
      typeof window.DriverApp.postMessage !== "function" ||
      !pharmacyId ||
      !driverID ||
      data?.id == null ||
      !resolvedPharmacistId
    ) {
      return false;
    }

    try {
      window.DriverApp.postMessage(
        JSON.stringify({
          type: "webLog",
          message: "driver dropdown prewarm: sending native event from video button",
          patientId: String(data?.id),
          eventType: "video-button",
        }),
      );
    } catch (_) {}

    try {
      window.DriverApp.postMessage(
        JSON.stringify({
          type: "videoCallPrewarmPatient",
          patientId: String(data.id),
          patientName: `${data?.last_name || ""} ${data?.first_name || ""}`.trim(),
          patientPreferredName: data?.preferred_name || preferredValue || "",
          pharmacyId: String(pharmacyId),
          driverID: String(driverID),
          driverName,
          pharmacistId: String(resolvedPharmacistId),
          pharmacistName: selectedPharmacistName || "Pharmacist",
          authToken: Cookies.get("authToken") ?? null,
          ablyApiKey: EndPoints.apiKey || null,
        }),
      );
      return true;
    } catch (error) {
      console.error("Video Call: Failed to post native prewarm payload", error);
      return false;
    }
  };

  const postNativeVideoCallStart = (
    initiateData,
    resolvedPharmacistId,
    { omitInitiateData = false } = {},
  ) => {
    if (
      !inFlutterApp ||
      typeof window === "undefined" ||
      !window.DriverApp ||
      typeof window.DriverApp.postMessage !== "function"
    ) {
      return false;
    }

    const pharmacistId =
      resolvedPharmacistId ??
      initiateData?.pharmacist?.id ??
      initiateData?.pharmacist_id ??
      initiateData?.call?.pharmacist?.id ??
      initiateData?.call?.pharmacist_id ??
      pharmacistIdForSelectedPharmacy;
    if (!pharmacistId) {
      console.error("Video Call: Missing pharmacist id for Flutter bridge");
      return false;
    }

    const pharmacistName =
      initiateData?.pharmacist?.name ??
      initiateData?.pharmacist_name ??
      initiateData?.pharmacistName ??
      initiateData?.call?.pharmacist?.name ??
      selectedPharmacistName ??
      "Pharmacist";
    const authToken = Cookies.get("authToken") ?? null;
    const normalizedInitiateData = {
      ...initiateData,
      pharmacyId: String(pharmacyId),
      pharmacy_id: initiateData?.pharmacy_id ?? String(pharmacyId),
      driverId: String(driverID),
      driver_id: initiateData?.driver_id ?? String(driverID),
      driverName,
      driver_name: initiateData?.driver_name ?? driverName,
      pharmacistId: String(pharmacistId),
      pharmacist_id: initiateData?.pharmacist_id ?? String(pharmacistId),
      pharmacistName,
      pharmacist_name: initiateData?.pharmacist_name ?? pharmacistName,
      pharmacist:
        initiateData?.pharmacist && typeof initiateData.pharmacist === "object"
          ? initiateData.pharmacist
          : {
              id: String(pharmacistId),
              name: pharmacistName,
            },
      driver:
        initiateData?.driver && typeof initiateData.driver === "object"
          ? initiateData.driver
          : {
              id: String(driverID),
              name: driverName,
            },
    };

    try {
      try {
        window.DriverApp.postMessage(
          JSON.stringify({
            type: "webLog",
            message: "DriverAppReact: posting full native call payload",
            patientId: String(data?.id),
          }),
        );
      } catch (_) {}
      const payload = {
        type: "videoCallStartNative",
        patientId: String(data?.id),
        patientName: `${data?.last_name || ""} ${data?.first_name || ""}`.trim(),
        patientPreferredName: isPharmacistForSelectedPharmacy
          ? data?.preferred_name || preferredValue || ""
          : "",
        pharmacyId: String(pharmacyId),
        driverID: String(driverID),
        driverName,
        pharmacistId: String(pharmacistId),
        pharmacistName,
        authToken,
        ablyApiKey: EndPoints.apiKey || null,
      };
      if (!omitInitiateData) {
        payload.initiateData = normalizedInitiateData;
      }
      window.DriverApp.postMessage(JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error("Video Call: Failed to post native call payload", error);
      return false;
    }
  };

  // Check if any RX item has is_din_matched.status === 1 and (signature_required === 1 or signature_complete === 0)
  const checkVideoCallCondition = () => {
    // Only show video call icon if logged in user is a driver (not a supervisor)
    // Check if driver_profile exists and user is not a supervisor
    if (!driverID || isPharmacistForSelectedPharmacy) return false;

    const rxDataArrays = [
      data?.patients_win_rx_data,
      data?.patients_win_rx_data_delivered,
      data?.patients_win_rx_data_today_missed,
      data?.patients_win_rx_data_final_missed,
      data?.rx_data,
    ];
    // console.log("rxDataArrays", rxDataArrays);
    for (const rxArray of rxDataArrays) {
      if (Array.isArray(rxArray)) {
        const hasMatch = rxArray.some((rx) => {
          // const isDinMatched = rx?.is_din_matched?.status === 1;
          // const rxwitness = (rx?.rx_witness || "").toUpperCase() === "Y";
          return (
            // isDinMatched && rxwitness && data?.delivery_status !== "delivered"
            data?.delivery_status !== "delivered"
          );
        });
        if (hasMatch) return true;
      }
    }
    return false;
  };

  const showVideoCallIcon = checkVideoCallCondition();
  const bypassCameraCheckForDev = process.env.NODE_ENV === "development";
  // In dev over LAN HTTP, browsers often hide camera devices entirely because the context is not secure.
  const showVideoCallButton =
    showVideoCallIcon &&
    !hide &&
    !isPharmacistForSelectedPharmacy 
    // &&
    // (hasCamera || bypassCameraCheckForDev);

  // Detect if device has a camera (so we only show video call icon when video is possible)
  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasCamera(false);
      return () => {};
    }
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (cancelled) return;
        const videoInputs = devices.filter((d) => d.kind === "videoinput");
        setHasCamera(videoInputs.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasCamera(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup Ably connections and timeout on unmount
  useEffect(() => {
    return () => {
      if (ablyRef.current && ablyRef.current.connection.state === "connected") {
        ablyRef.current.close();
      }
      if (
        responseAblyRef.current &&
        responseAblyRef.current.connection.state === "connected"
      ) {
        responseAblyRef.current.close();
      }
      if (
        sessionAblyRef.current &&
        sessionAblyRef.current.connection.state === "connected"
      ) {
        sessionAblyRef.current.close();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (graceTimeoutRef.current) {
        clearTimeout(graceTimeoutRef.current);
      }
    };
  }, []);

  // Keep latest call state in a ref (avoid stale closures in timeouts)
  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const handleVideoCallClick = async () => {
    // New call: clear any previous status reason (e.g. busy/timeout) and error
    setCallStatusReason(null);
    setVideoCallError(null);

    if (!pharmacyId || !driverID || data?.id == null) {
      console.error("Video Call: Missing required information", {
        pharmacyId,
        driverID,
        patientId: data?.id,
      });
      setVideoCallError("Something went wrong");
      setTimeout(() => setVideoCallError(null), 5000);
      return;
    }

    if (inFlutterApp && pharmacistIdForSelectedPharmacy) {
      postNativeVideoCallPrewarm(pharmacistIdForSelectedPharmacy);
      const posted = postNativeVideoCallStart(
        {
          status: "requesting",
          pharmacy_id: String(pharmacyId),
          driver_id: String(driverID),
          driver_name: driverName,
          pharmacist_id: String(pharmacistIdForSelectedPharmacy),
          pharmacist_name: selectedPharmacistName ?? "Pharmacist",
        },
        pharmacistIdForSelectedPharmacy,
        { omitInitiateData: true },
      );
      if (posted) {
        dispatch(clearVideoCallInitiate());
        setCallState("idle");
        setIsVideoCallOpen(false);
        return;
      }
    }

    setVideoCallLoadingPatientId(data?.id ?? null);
    let initiateData;
    try {
      initiateData = await dispatch(
        initiateVideoCallToPharmacist({
          pharmacy_id: pharmacyId,
          patient_id: data?.id,
        }),
      ).unwrap();
    } catch (apiError) {
      console.error("Error initiating video call (API):", apiError);
      setVideoCallLoadingPatientId(null);
      const busyReason =
        apiError?.data?.reason === "busy" ||
        String(apiError?.data?.message || "")
          .toLowerCase()
          .includes("another call");
      if (busyReason) {
        if (inFlutterApp) {
          setVideoCallError("Pharmacist is busy");
          setTimeout(() => setVideoCallError(null), 5000);
          dispatch(clearVideoCallInitiate());
          return;
        }
        setCallStatusReason("busy");
        setCallState("declined");
        setIsVideoCallOpen(true);
        dispatch(clearVideoCallInitiate());
        setTimeout(() => {
          setCallState("idle");
          setIsVideoCallOpen(false);
        }, 3500);
        return;
      }
      setVideoCallError("Something went wrong");
      setTimeout(() => setVideoCallError(null), 5000);
      return;
    } finally {
      setVideoCallLoadingPatientId(null);
    }

    const pharmacistId =
      initiateData?.pharmacist?.id ??
      initiateData?.pharmacist_id ??
      initiateData?.call?.pharmacist?.id ??
      initiateData?.call?.pharmacist_id ??
      pharmacistIdForSelectedPharmacy;
    if (!pharmacistId) {
      console.error("Video Call: API did not return pharmacist id");
      setVideoCallError("Something went wrong");
      setTimeout(() => setVideoCallError(null), 5000);
      return;
    }

    if (inFlutterApp) {
      const callId =
        initiateData?.call_id ??
        initiateData?.vonage_session_id ??
        `call-${Date.now()}-${driverID}`;

      try {
        await publishVideoCallRequest({
          pharmacyId,
          pharmacistId,
          driverId: driverID,
          driverName,
          initiateApiResponse: initiateData,
          callId,
        });
      } catch (signalError) {
        console.error(
          "Error publishing video call request for Flutter native flow:",
          signalError,
        );
      }

      const posted = postNativeVideoCallStart(initiateData, pharmacistId);
      dispatch(clearVideoCallInitiate());
      setCallState("idle");
      setIsVideoCallOpen(false);
      if (!posted) {
        setVideoCallError("Something went wrong");
        setTimeout(() => setVideoCallError(null), 5000);
      }
      return;
    }

    setCallState("requesting");
    setIsVideoCallOpen(true);

    const callId =
      initiateData?.call_id ??
      initiateData?.vonage_session_id ??
      `call-${Date.now()}-${driverID}`;
    callInfoRef.current = {
      pharmacyId,
      pharmacistId,
      callId,
      driverId: driverID,
      driverName,
      patientId: data?.id,
      patientName: `${data?.first_name} ${data?.last_name}`,
      patientPreferredName: isPharmacistForSelectedPharmacy
        ? data?.preferred_name || preferredValue || ""
        : "",
    };

    try {
      // Subscribe to response channel BEFORE publishing request so we don't miss a fast "declined (busy)" from pharmacist
      const responseAbly = subscribeToVideoCallResponse({
        pharmacyId,
        driverId: driverID,
        onResponse: (response) => {
          if (response.callId === callId) {
            if (response.status === "accepted") {
              setCallStatusReason(null);
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
              if (graceTimeoutRef.current) {
                clearTimeout(graceTimeoutRef.current);
                graceTimeoutRef.current = null;
              }
              setCallState("accepted");
              callInfoRef.current = null;
              activeCallIdRef.current = callId;
              const sessionAbly = subscribeToVideoCallSession(
                callId,
                () => {
                  setCallState("idle");
                  setIsVideoCallOpen(false);
                  activeCallIdRef.current = null;
                  dispatch(clearVideoCallInitiate());
                  sessionAblyRef.current?.close?.();
                  sessionAblyRef.current = null;
                },
                { pharmacyId },
              );
              sessionAblyRef.current = sessionAbly;
            } else if (response.status === "declined") {
              // If pharmacist declined because they are already in another call,
              // propagate that reason so driver sees an accurate message.
              setCallStatusReason(response.reason || null);
              setCallState("declined");
              callInfoRef.current = null;
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }
              dispatch(clearVideoCallInitiate());
              setTimeout(() => {
                setCallState("idle");
                setIsVideoCallOpen(false);
              }, 3000);
            }
          }
        },
      });
      responseAblyRef.current = responseAbly;

      await publishVideoCallRequest({
        pharmacyId,
        driverId: driverID,
        driverName,
        initiateApiResponse: initiateData,
        callId,
      });

      const RING_TIMEOUT_MS = 30000;
      const GRACE_AFTER_RING_MS = 2500; // After ring timeout, wait for late "accepted" before disconnecting driver

      timeoutRef.current = setTimeout(() => {
        const info = callInfoRef.current;
        const stillRequesting =
          callStateRef.current === "requesting" &&
          info?.callId === callId &&
          activeCallIdRef.current == null;

        if (!stillRequesting) return;

        timeoutRef.current = null;

        if (info) {
          cancelDriverDeliveryVideoCall(info.callId, "timeout").catch((err) =>
            console.error("Error cancelling call after timeout:", err),
          );
          publishVideoCallCancelled({
            pharmacyId: info.pharmacyId,
            pharmacistId: info.pharmacistId,
            callId: info.callId,
            driverId: info.driverId,
            driverName: info.driverName,
            patientId: info.patientId,
            patientName: info.patientName,
          }).catch((err) =>
            console.error(
              "Error publishing call cancelled after timeout:",
              err,
            ),
          );
        }

        const doDisconnect = () => {
          if (activeCallIdRef.current != null) return; // Accepted in the meantime
          setCallStatusReason("timeout");
          setCallState("ended");
          if (info?.callId) {
            publishVideoCallEnded(info.callId, "driver").catch((err) =>
              console.error("Error publishing call ended after timeout:", err),
            );
          }
          callInfoRef.current = null;
          dispatch(clearVideoCallInitiate());
          setTimeout(() => {
            setIsVideoCallOpen(false);
            setCallState("idle");
          }, 3000);
          if (ablyRef.current?.connection?.state === "connected") {
            ablyRef.current.close();
          }
          if (responseAblyRef.current?.connection?.state === "connected") {
            responseAblyRef.current.close();
          }
        };

        // Grace period: keep driver listening for "accepted". If it arrives, we connect; otherwise disconnect.
        graceTimeoutRef.current = setTimeout(() => {
          graceTimeoutRef.current = null;
          doDisconnect();
        }, GRACE_AFTER_RING_MS);
      }, RING_TIMEOUT_MS);
    } catch (error) {
      console.error("Error initiating video call:", error);
      setCallState("idle");
      setIsVideoCallOpen(false);
      dispatch(clearVideoCallInitiate());
      setVideoCallError("Something went wrong");
      setTimeout(() => setVideoCallError(null), 5000);
    }
  };

  const handleVideoCallClose = () => {
    const info = callInfoRef.current;
    const activeCallId = activeCallIdRef.current;
    if (activeCallId) {
      endDriverDeliveryVideoCall(activeCallId).catch((err) =>
        console.error("Error ending active call:", err),
      );
      publishVideoCallEnded(activeCallId, "driver").catch((err) =>
        console.error("Error publishing active call ended:", err),
      );
      activeCallIdRef.current = null;
    }
    if (info) {
      cancelDriverDeliveryVideoCall(
        info.callId,
        callStateRef.current === "requesting" ? "driver_cancelled" : "driver",
      ).catch((err) => console.error("Error cancelling call:", err));
      publishVideoCallCancelled({
        pharmacyId: info.pharmacyId,
        pharmacistId: info.pharmacistId,
        callId: info.callId,
        driverId: info.driverId,
        driverName: info.driverName,
        patientId: info.patientId,
        patientName: info.patientName,
      }).catch((err) => console.error("Error publishing call cancelled:", err));
      publishVideoCallEnded(info.callId, "driver").catch((err) =>
        console.error("Error publishing pending call ended:", err),
      );
      callInfoRef.current = null;
    }
    setIsVideoCallOpen(false);
    setCallState("idle");
    setCallStatusReason(null);
    if (ablyRef.current && ablyRef.current.connection.state === "connected") {
      ablyRef.current.close();
      ablyRef.current = null;
    }
    if (
      responseAblyRef.current &&
      responseAblyRef.current.connection.state === "connected"
    ) {
      responseAblyRef.current.close();
      responseAblyRef.current = null;
    }
    if (
      sessionAblyRef.current &&
      sessionAblyRef.current.connection.state === "connected"
    ) {
      sessionAblyRef.current.close();
      sessionAblyRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (graceTimeoutRef.current) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    dispatch(clearVideoCallInitiate());
  };

  const handleSaveScreenshot = async (blob) => {
    if (!pharmacyId || data?.id == null || !blob) return;
    try {
      await dispatch(
        saveDeliveryCallImage({
          pharmacy_id: pharmacyId,
          patient_id: data.id,
          imageBlob: blob,
        }),
      ).unwrap();
    } catch (err) {}
  };

  const isBusy =
    callState === "requesting" || videoCallLoadingPatientId === data?.id;
  const isDisabled = disabled || isBusy;
  const isCta = variant === "cta";

  return (
    <div className={isCta ? "flex-none" : "ml-auto"}>
      <div className="">
        {showVideoCallButton && (
          <button
            type="button"
            className={
              isCta
                ? `px-2 py-2 h-10 w-[48px] flex-none rounded-md whitespace-nowrap ${
                    isDisabled
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-100 text-white"
                  } ${className}`.trim()
                : `px-1 py-1.5 rounded-md bg-transparent text-green-500 relative z-0  min-h-[1.5rem] flex items-center justify-center ${className}`.trim()
            }
            onClick={() => {
              if (isDisabled) return;
              handleVideoCallClick();
            }}
            data-patient-id={data?.id != null ? String(data.id) : ""}
            data-patient-name={`${data?.last_name || ""} ${data?.first_name || ""}`.trim()}
            data-pharmacy-id={pharmacyId != null ? String(pharmacyId) : ""}
            data-driver-id={driverID != null ? String(driverID) : ""}
            data-driver-name={driverName || ""}
            data-pharmacist-id={
              pharmacistIdForSelectedPharmacy != null
                ? String(pharmacistIdForSelectedPharmacy)
                : ""
            }
            data-pharmacist-name={selectedPharmacistName || ""}
            disabled={isDisabled}
          >
            {isCta ? (
              <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                <span className="w-6 h-6 pointer-events-none">
                  <Icons.VideoIcon className="w-6 h-6" color="#2563EB" />
                </span>
                {/* <span className="font-medium whitespace-nowrap">{label}</span> */}
              </span>
            ) : videoCallLoadingPatientId === data?.id ? (
              <span className="flex items-center justify-center gap-0.5">
                <span
                  className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce"
                  style={{ animationDelay: "0ms" }}
                />
                <span
                  className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce"
                  style={{ animationDelay: "150ms" }}
                />
                <span
                  className="w-1.5 h-1.5 rounded-full bg-green-500 animate-bounce"
                  style={{ animationDelay: "300ms" }}
                />
              </span>
            ) : (
              <span className="w-6 h-6 relative z-0 pointer-events-none">
                <Icons.VideoIcon className="w-6 h-6" color="currentColor" />
              </span>
            )}
            {!isCta &&
              callState === "requesting" &&
              videoCallLoadingPatientId !== data?.id && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse z-10"></span>
              )}
            {!isCta && callState === "declined" && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full z-10"></span>
            )}
          </button>
        )}
      </div>

      {/* Video Call Error Message */}
      {videoCallError && (
        <ErrorMessage
          setShowToast={() => setVideoCallError(null)}
          message={videoCallError}
        />
      )}

      {/* Video Call Component */}
      <VideoCallErrorBoundary onClose={handleVideoCallClose}>
        <VideoCall
          isOpen={isVideoCallOpen}
          onClose={handleVideoCallClose}
          patientData={data}
          driverName={driverName}
          pharmacistName={videoCallInitiateData?.pharmacist?.name}
          callState={callState}
          statusReason={callStatusReason}
          pharmacistBusy={Boolean(videoCallInitiateData?.pharmacist_busy)}
          isCaller={true}
          vonageSessionId={videoCallInitiateData?.vonage_session_id}
          vonageToken={videoCallInitiateData?.vonage_token}
          applicationId={videoCallInitiateData?.application_id}
          videoProvider={videoCallInitiateData?.video_provider}
          livekitUrl={videoCallInitiateData?.livekit_url}
          onScreenshot={handleSaveScreenshot}
        />
      </VideoCallErrorBoundary>
    </div>
  );
};
